import React from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import PageHeader from "@/components/PageHeader";
import { TermLabel, GridBackground } from "@/components/Effects";

export default function Economy() {
  const { t, lang } = useLanguage();
  const flow = t("economy.flow");
  const sections = t("economy.sections");

  return (
    <div data-testid="economy-page" className="relative overflow-hidden">
      <GridBackground />
      <PageHeader
        label={t("economy.label")}
        title={t("economy.title")}
        sub={t("economy.sub")}
      />

      {/* Value flow chain */}
      <section className="relative mx-auto max-w-7xl px-4 pt-16 sm:px-6">
        <TermLabel className="mb-8 justify-center text-center">
          {t("economy.flowTitle")}
        </TermLabel>
        <div className="mx-auto flex max-w-xs flex-col items-center" data-testid="economy-flow">
          {flow.map((node, i) => (
            <React.Fragment key={node}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className={`mizu-card w-full border px-6 py-3.5 text-center font-display text-sm font-bold tracking-[0.25em] ${
                  node === "ARC"
                    ? "border-mizu-cyan/70 bg-mizu-cyan/10 text-mizu-cyan shadow-[0_0_24px_rgba(0,240,255,0.15)]"
                    : "border-mizu-cyan/20 bg-mizu-elevated/40 text-mizu-silver"
                } ${lang === "jp" ? "font-jp tracking-[0.2em]" : ""}`}
                data-testid={`flow-node-${i}`}
              >
                {node}
              </motion.div>
              {i < flow.length - 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 + 0.2 }}
                  className="relative flex h-8 items-center justify-center"
                >
                  <div className="h-full w-px bg-mizu-cyan/25" />
                  <ArrowDown
                    size={12}
                    className="absolute animate-bounce text-mizu-cyan/70"
                    style={{ animationDuration: "2.4s" }}
                  />
                </motion.div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Sections */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {sections.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
              className={`mizu-panel mizu-card mizu-corners p-6 ${i === 0 ? "lg:col-span-1" : ""}`}
              data-testid={`economy-section-${i}`}
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="font-mono text-[9px] tracking-[0.3em] text-mizu-faint">
                  SYS.{String(i + 1).padStart(2, "0")}
                </span>
                <span className="h-1.5 w-1.5 bg-mizu-cyan/60" />
              </div>
              <h3 className={`font-display text-sm font-bold tracking-[0.18em] text-white ${lang === "jp" ? "font-jp tracking-wider" : ""}`}>
                {s.title}
              </h3>
              <p className="mt-3 font-mono text-[11px] leading-6 text-mizu-dim">
                {s.body}
              </p>
            </motion.div>
          ))}

          {/* closing statement card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: 0.2 }}
            className="mizu-panel mizu-card mizu-corners flex flex-col justify-center border-mizu-cyan/40 p-6"
          >
            <p className="font-mono text-xs leading-6 text-mizu-cyan mizu-glow">
              &gt; A MORE OPEN ECONOMY.
            </p>
            <p className="mt-2 font-mono text-xs leading-6 text-mizu-silver">
              &gt; THE FUTURE IS ALREADY ONLINE
              <span className="animate-blink">_</span>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
